import React, { useState } from 'react'
import { Box, Grid, TextField } from '@mui/material'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { message } from 'antd'
import Button from '@mui/material/Button'
import UploadIcon from '@mui/icons-material/Upload'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import useAxios, { axiosInstance } from '../../../../../../../hooks/useAxios'
import CircularLoading from '../../../../../../../components/CircularProgress'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../../../components/SelectSearch'
import { IUserLists } from '../../../../../../../types/user'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import { IDistributionChannel } from '../../../../../../../types/distribution-channel'
import { ICategory } from '../../../../../../../types/category'
import { validateMinMaxValue } from '../../../../../../../utils/validateMinMaxValue'
import { IPipeline } from '../../../../../../../types/pipeline'
import { ONLY_DATE_FORMAT } from '../../../../../../../constants/global'
import { useNavigate } from 'react-router-dom'
import PageUrls from '../../../../../../../constants/page-urls'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'
import UserSelectSearch from '../../../../../../../components/SelectSearch/UserSelectSearch'
import ChannelSelectSearch from '../../../../../../../components/SelectSearch/ChannelSelectSearch'
import PipelineSelectSearch from '../../../../../../../components/SelectSearch/PipelineSelectSearch'
import CategorySelectSearch from '../../../../../../../components/SelectSearch/CategorySelectSearch'
import ProductSelectSearch from '../../../../../../../components/SelectSearch/ProductSelectSearch'

interface IImportForm {
  categoryObjectId: string
  channelObjectId: string
  date: string
  expiredDays: number
  pipelineObjectId: string
  assignToObjectId: string
  product: string
}

const getImportFormValidationSchema = () => {
  return Yup.object({
    categoryObjectId: Yup.string().required('กรุณาเลือกหมวดหมู่'),
    channelObjectId: Yup.string(),
    date: Yup.string().required('กรุณาเลือกวันที่'),
    expiredDays: Yup.number()
      .min(1, 'กรุณากรอกจำนวนวันที่หมดอายุมากกว่า 1 วัน')
      .max(1000, 'กรุณากรอกจำนวนวันที่หมดอายุน้อยกว่า 1000 วัน')
      .required('กรุณากรอกจำนวนวันที่หมดอายุ'),
    pipelineObjectId: Yup.string().required('กรุณาเลือกท่อ'),
    assignToObjectId: Yup.string(),
  })
}
const LeadImportForm = () => {
  const navigate = useNavigate()
  const [searchUserTelesales, setSearchUserTelesales] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchPipeline, setSearchPipeline] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')
  const userTelesalesList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchUserTelesales,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchUserTelesales = async (value: string) => {
    setSearchUserTelesales(value)
    await userTelesalesList.mutate()
  }

  const distributionChannels = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      name: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    await distributionChannels.mutate()
  }

  const categories = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categories.mutate()
  }

  const pipelineList = useAxios<PaginationResponseInterface<IPipeline>>({
    method: 'GET',
    url: '/pipeline',
    params: {
      page: 1,
      perPage: 5,
      search: searchPipeline,
      startDate: '',
      endDate: '',
    },
  })
  const handleSearchPipeline = async (value: string) => {
    setSearchPipeline(value)
    await pipelineList.mutate()
  }

  const formik = useFormik<IImportForm>({
    initialValues: {
      categoryObjectId: '',
      channelObjectId: '',
      date: '',
      expiredDays: 1,
      pipelineObjectId: '',
      assignToObjectId: '',
      product: '',
    } as IImportForm,
    validationSchema: getImportFormValidationSchema(),
    onSubmit: async (values: IImportForm) => {
      setIsLoading(true)
      try {
        const response = await axiosInstance.post('/lead/imports', {
          ...values,
          date: dayjs(values.date).format('YYYY-MM-DD'),
        })
        if (response.data.some((item: any) => item.status === 'success')) {
          setResetAutoComplete(new Date().getTime().toString())
          formik.resetForm()
          message.success(
            `นำเข้าข้อมูลสำเร็จ ${
              response.data.filter((item: any) => item.status === 'success')
                .length
            } รายการ`,
          )
          navigate(PageUrls.CRM.Lead.List)
        } else {
          message.warning(`ไม่สามารถนำเข้าซ้ำได้`)
        }
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} justifyContent="space-between">
          <Grid item xs={12} lg={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format={ONLY_DATE_FORMAT}
                // disableFuture
                label="วันที่สั่งซื้อ"
                name="orderDate"
                value={formik.values.date ? dayjs(formik.values.date) : null}
                onChange={(e) => {
                  formik.setFieldValue('date', e?.format())
                }}
                sx={{ width: '100%' }}
                slotProps={{
                  textField: {
                    helperText:
                      formik.touched.date && (formik.errors.date as string),
                    error: !!(formik.touched.date && formik.errors.date),
                    onBlur: formik.handleBlur,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} lg={3}>
            <CategorySelectSearch
              multiple={false}
              resetAutoComplete={resetAutoComplete}
              label={'เลือกหมวดหมู่สินค้า'}
              onSelect={(e) =>
                formik.setFieldValue('categoryObjectId', e?.value.objectId)
              }
              name="categoryObjectId"
              helperText={
                formik.touched.categoryObjectId &&
                (formik.errors.categoryObjectId as string)
              }
              error={
                !!(
                  formik.touched.categoryObjectId &&
                  formik.errors.categoryObjectId
                )
              }
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <ProductSelectSearch
              multiple={false}
              name="product"
              resetAutoComplete={resetAutoComplete}
              onSelect={(e) =>
                e && formik.setFieldValue('product', e?.value.objectId)
              }
              error={!!(formik.touched.product && formik.errors.product)}
              helperText={
                formik.touched.product && (formik.errors.product as string)
              }
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            {/* <SelectSearch<IDistributionChannel>
              resetAutoComplete={resetAutoComplete}
              label={'เลือกช่องทางการขาย'}
              onSelect={(e) =>
                formik.setFieldValue('channelObjectId', e?.value.objectId)
              }
              search={searchDistributionChannel}
              onSearch={handleSearchDistributionChannel}
              options={
                distributionChannels.data?.records.map(
                  (distributionChannel: IDistributionChannel) => ({
                    label: distributionChannel.name,
                    value: distributionChannel,
                  }),
                ) || ([] as IOptionSelect<IDistributionChannel>[])
              }
              name="channelObjectId"
              helperText={
                formik.touched.channelObjectId &&
                (formik.errors.channelObjectId as string)
              }
              error={
                !!(
                  formik.touched.channelObjectId &&
                  formik.errors.channelObjectId
                )
              }
              onBlur={formik.handleBlur}
            /> */}
            <ChannelSelectSearch
              resetAutoComplete={resetAutoComplete}
              label={'เลือกช่องทางการขาย'}
              onSelect={(e) =>
                formik.setFieldValue('channelObjectId', e?.value.objectId)
              }
              multiple={false}
              name="channelObjectId"
              helperText={
                formik.touched.channelObjectId &&
                (formik.errors.channelObjectId as string)
              }
              error={
                !!(
                  formik.touched.channelObjectId &&
                  formik.errors.channelObjectId
                )
              }
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            {/* <SelectSearch<IPipeline>
              resetAutoComplete={resetAutoComplete}
              label={'เลือก pipeline'}
              onSelect={(e) =>
                formik.setFieldValue('pipelineObjectId', e?.value.objectId)
              }
              search={searchPipeline}
              onSearch={handleSearchPipeline}
              options={
                pipelineList.data?.records.map((pipeline: IPipeline) => ({
                  label: pipeline.name,
                  value: pipeline,
                })) || ([] as IOptionSelect<IPipeline>[])
              }
              name="pipelineObjectId"
              helperText={
                formik.touched.pipelineObjectId &&
                (formik.errors.pipelineObjectId as string)
              }
              error={
                !!(
                  formik.touched.pipelineObjectId &&
                  formik.errors.pipelineObjectId
                )
              }
              onBlur={formik.handleBlur}
            /> */}
            <PipelineSelectSearch
              multiple={false}
              resetAutoComplete={resetAutoComplete}
              onSelect={(e) =>
                formik.setFieldValue('pipelineObjectId', e?.value.objectId)
              }
              name="pipelineObjectId"
              helperText={
                formik.touched.pipelineObjectId &&
                (formik.errors.pipelineObjectId as string)
              }
              error={
                !!(
                  formik.touched.pipelineObjectId &&
                  formik.errors.pipelineObjectId
                )
              }
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            {/* <SelectSearch<IUserLists>
              resetAutoComplete={resetAutoComplete}
              label={'กำหนดผู้ดูแล Lead'}
              onSelect={(e) =>
                formik.setFieldValue('assignToObjectId', e?.value.objectId)
              }
              search={searchUserTelesales}
              onSearch={handleSearchUserTelesales}
              options={
                userTelesalesList.data?.records
                  .filter(
                    (userTelesales: IUserLists) => userTelesales.isAvailable,
                  )
                  .map((userTelesales: IUserLists) => ({
                    label: userTelesales.name,
                    value: userTelesales,
                  })) || ([] as IOptionSelect<IUserLists>[])
              }
              name="assignToObjectId"
              helperText={
                formik.touched.assignToObjectId &&
                (formik.errors.assignToObjectId as string)
              }
              error={
                !!(
                  formik.touched.assignToObjectId &&
                  formik.errors.assignToObjectId
                )
              }
              onBlur={formik.handleBlur}
            /> */}
            <UserSelectSearch
              label={'กำหนดผู้ดูแล Lead'}
              onSelect={(e) =>
                formik.setFieldValue('assignToObjectId', e?.value.objectId)
              }
              name="assignToObjectId"
              helperText={
                formik.touched.assignToObjectId &&
                (formik.errors.assignToObjectId as string)
              }
              error={
                !!(
                  formik.touched.assignToObjectId &&
                  formik.errors.assignToObjectId
                )
              }
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              id="expiredDays"
              label="จำนวนวันที่หมดอายุ (วัน)"
              name="expiredDays"
              value={formik.values.expiredDays}
              onBlur={formik.handleBlur}
              type={'number'}
              onKeyPress={(e) => {
                if (['+', '-', 'e', '*', '.'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={(e) =>
                formik.setFieldValue(
                  'expiredDays',
                  validateMinMaxValue(e.target.value, 0, 1000) || '',
                )
              }
              helperText={
                formik.touched.expiredDays &&
                (formik.errors.expiredDays as string)
              }
              error={
                !!(formik.touched.expiredDays && formik.errors.expiredDays)
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2} justifyContent={'center'}>
          <Grid item>
            <Button
              startIcon={<UploadIcon />}
              color="primary"
              type="submit"
              size="large"
              variant="contained"
            >
              นำเข้าข้อมูล
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default LeadImportForm
